import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { createExactValues, mergeDocuments, needsExactValues } from "./exactValues";

import styles from "./scss/examples.module.scss";

const EMPTY = { docs: [], chars: 0 };
const NONE = {
  count: 0,
  exact: undefined,
  add: () => ({ kept: 0, dropped: 0 }),
  clear: () => {}
};

const ExamplesContext = createContext(null);

// The example JSON documents imported in Keys and Values, kept in memory for the
// session of the editor: not in the form, not saved with the pipeline. With them
// the rules get exact values with their own selectors applied; without them the
// flat values of the schema.
export function ExamplesProvider({ children }) {
  const { values } = useFormikContext();
  const [state, setState] = useState(EMPTY);
  const stateRef = useRef(state);

  const add = useCallback((json, chars) => {
    const result = mergeDocuments(stateRef.current, json, chars);
    stateRef.current = result.state;
    setState(result.state);

    return result;
  }, []);

  const clear = useCallback(() => {
    stateRef.current = EMPTY;
    setState(EMPTY);
  }, []);

  // Resetting the keys (all of them gone) empties the documents too
  const keysCount = Object.keys(values.keysAndValues?.keys ?? {}).length;
  const prevKeysCount = useRef(keysCount);
  useEffect(() => {
    if (prevKeysCount.current > 0 && keysCount === 0) clear();
    prevKeysCount.current = keysCount;
  }, [clear, keysCount]);

  // The documents are about the manual keys and are not modified by this form.
  const manual = values.keysAndValues?.mode === "manual";
  const exact = useMemo(
    () => (manual && state.docs.length > 0 ? createExactValues(state.docs) : undefined),
    [manual, state.docs]
  );

  const value = useMemo(
    () => ({ count: state.docs.length, exact, add, clear }),
    [add, clear, exact, state.docs.length]
  );

  return <ExamplesContext.Provider value={value}>{children}</ExamplesContext.Provider>;
}

/**
 * The example documents in memory: `count`, `exact` (the exact values of a key,
 * undefined without documents), `add(json, chars)` and `clear()`. Outside the
 * pipeline editor there are none.
 */
export function useExamples() {
  return useContext(ExamplesContext) ?? NONE;
}

/**
 * Where the values of the rules are chosen: without example documents the
 * values of filtered lists and of keys with selectors are not correlated.
 */
export function ValuesNote() {
  const { values } = useFormikContext();
  const { count } = useExamples();

  if (count > 0 || !needsExactValues(values)) return null;

  return (
    <div className={styles.valuesNote}>
      Values are not correlated: load example JSON documents in Keys and Values for exact values
      with filters and selectors
    </div>
  );
}
